import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { concatMap, filter, map, switchMap, take } from 'rxjs/operators';
import { environment } from 'oneok/environments/environment';
import { marked } from 'marked';
import { BehaviorSubject, lastValueFrom, Observable } from 'rxjs';
import { AppUser } from '@legacy-models/strapi/AppUser';
import { AppNotification } from '@legacy-models/strapi/AppNotification';
import { AppOrientation } from '@legacy-models/strapi/AppOrientation';

interface AppUserRecord {
  id: number;
  crmId: string;
}

interface StrapiMediaRelation {
  data?: {
    attributes?: {
      url?: string;
    };
  };
}

interface StrapiFeaturePerk {
  Perk: string;
}

interface StrapiFeatureItem {
  Title: string;
  Perk: StrapiFeaturePerk[];
  Icon?: StrapiMediaRelation;
}

interface StrapiBuzzwordItem {
  Word: string;
}

interface StrapiContactStatusItem {
  Name: string;
  ColorBg: string;
  ColorFg: string;
  Label: string;
  Description: string;
}

interface StrapiFeatureCardItem {
  Name: string;
  Description: string;
  Role: string;
  URL: string;
  Icon?: StrapiMediaRelation;
}

interface StrapiDocumentItem {
  DocumentName: string;
  DocumentContext: string;
  DocumentUrl?: string;
  DocumentFile?: StrapiMediaRelation;
}

interface StrapiMetaItem {
  id: number;
  acknowledgedAt?: string;
  [key: string]: unknown;
}

interface StrapiNotificationAttributes {
  Body?: string;
  Title?: string;
  ViewUrl?: string;
  Meta?: StrapiMetaItem[];
  IsActive?: boolean;
  IsViewStatusSaved?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface StrapiNotificationEntry {
  id: number;
  attributes: StrapiNotificationAttributes;
}

interface StrapiFaqItem {
  Question: string;
  Answer: string;
  Category: string;
}

interface StrapiContactItem {
  FullName: string;
  Title: string;
  Area: string;
  Group: string;
  Email: string;
  PhoneMobile: string;
  PhoneOffice: string;
}

interface StrapiHelpFeatureItem {
  FeatureName: string;
  Faqs?: StrapiFaqItem[];
  Contacts?: StrapiContactItem[];
}

interface StrapiHelpSection {
  attributes: {
    Description: string;
    DisplayName: string;
    HelpFeature: StrapiHelpFeatureItem[];
  };
}

@Injectable({
  providedIn: 'root'
})
export class Strapi {
  urlBase: string;
  apiUrl: string;
  appNotifications: AppNotification[];
  appOrientations: AppOrientation[];
  private readonly _appUserId = new BehaviorSubject<number | null>(null);

  get appUserId(): number | null {
    return this._appUserId.value;
  }

  set appUserId(value: number | null) {
    this._appUserId.next(value);
  }

  get appUserId$(): Observable<number> {
    return this._appUserId.pipe(
      filter((id): id is number => id !== null),
      take(1)
    );
  }

  private readonly _http = inject(HttpClient);

  constructor() {
    const appUrl = window.location.href;
    const isLocal = appUrl.indexOf('localhost:') >= 0;
    const isDev = appUrl.indexOf('dynlab.oneok.com') >= 0;
    const isTest = appUrl.indexOf('dyntest.oneok.com') >= 0;
    const isQa = appUrl.indexOf('dynqa.oneok.com') >= 0;
    if (environment.production) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.urlBase = environment.strapi.url;
    } else if (isLocal) {
      this.urlBase = environment.strapi.url;
    } else if (isDev) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.urlBase = environment.strapi.url;
    } else if (isTest) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.urlBase = environment.strapi.url;
    } else if (isQa) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.urlBase = environment.strapi.url;
    }
    this.apiUrl = this.urlBase + '/api/';
  }

  private _getConvertedMarkdown(markdownString: string): string {
    if (!markdownString) {
      return '';
    }
    return (marked.parse(markdownString, { async: false }) as string).replace(/&#10;/g, '');
  }

  private _getAllAppUsers(): Promise<AppUserRecord[]> {
    const endpoint = `${this.apiUrl}app-users?fields[0]=id&fields[1]=CrmId`;
    return lastValueFrom(
      this._http.get<{ data: { id: number; attributes: { CrmId: string } }[] }>(endpoint).pipe(
        map(({ data: allAppUsers }) =>
          allAppUsers.map(({ id, attributes: { CrmId: crmId } }) => {
            return { id, crmId };
          })
        )
      )
    );
  }

  private _getAppUser(strapiId: number): Promise<Record<string, unknown> & { id: number }> {
    const endpoint = `${this.apiUrl}app-users/${strapiId}`;
    return lastValueFrom(
      this._http.get<{ data: { id: number; attributes: Record<string, unknown> } }>(endpoint).pipe(
        map(({ data: { id, attributes } }) => {
          const portalUser: Record<string, unknown> & { id: number } = { id };
          for (const prop in attributes) {
            if (Object.hasOwn(attributes, prop)) {
              portalUser[prop] = attributes[prop];
            }
          }
          return portalUser;
        })
      )
    );
  }

  private _createAppUser(appUserAttributes: AppUser): Promise<Record<string, unknown> & { id: number }> {
    const endpoint = `${this.apiUrl}app-users`;
    return lastValueFrom(
      this._http
        .post<{ data: { id: number; attributes: Record<string, unknown> } }>(endpoint, { data: appUserAttributes })
        .pipe(
          map(({ data: { id, attributes } }) => {
            const portalUser: Record<string, unknown> & { id: number } = { id };
            for (const prop in attributes) {
              if (Object.hasOwn(attributes, prop)) {
                portalUser[prop] = attributes[prop];
              }
            }
            return portalUser;
          })
        )
    );
  }

  getLoginContent(): Observable<any> {
    const endpoint = `${this.apiUrl}page-login`;
    return this._http.get(endpoint).pipe(
      map(
        ({
          data: {
            attributes: { Main: main }
          }
        }: any) => {
          return this._getConvertedMarkdown(main);
        }
      )
    );
  }

  getInterestedContent(): Observable<any> {
    const endpoint = `${this.apiUrl}page-interested?populate[0]=Buzzwords&populate[1]=Features&populate[2]=Features.Icon,Features.Perk`;
    return this._http
      .get<{
        data: {
          attributes: {
            Heading: string;
            Subheading: string;
            Buzzwords: StrapiBuzzwordItem[];
            Features: StrapiFeatureItem[];
          };
        };
      }>(endpoint)
      .pipe(
        map(
          ({
            data: {
              attributes: { Heading: heading, Subheading: subheading, Buzzwords: buzzwords, Features: features }
            }
          }) => {
            return {
              Heading: heading,
              Subheading: subheading,
              Buzzwords: buzzwords.map(({ Word: word }) => word),
              Features: features.map(({ Title: title, Perk: perk, Icon: icon }) => {
                const iconUrl = icon?.data?.attributes?.url ?? '';
                return {
                  Title: title,
                  Perks: perk.map(({ Perk: perkText }) => this._getConvertedMarkdown(perkText)),
                  Icon: iconUrl ? this.urlBase + iconUrl : ''
                };
              })
            };
          }
        )
      );
  }

  getBackgroundImages(): Observable<any> {
    const endpoint = `${this.apiUrl}global?populate=*`;
    return this._http.get(endpoint).pipe(
      map(
        ({
          data: {
            attributes: {
              UnauthenticatedBackgroundImage: unauthenticatedBackgroundImage,
              NggpBackgroundImage: nggpBackgroundImage,
              NgpBackgroundImage: ngpBackgroundImage
            }
          }
        }: any) => {
          const returnedObj: any = {};
          if (unauthenticatedBackgroundImage.data) {
            returnedObj.UnauthenticatedBackgroundImage =
              this.urlBase + unauthenticatedBackgroundImage.data.attributes.url;
          }
          if (nggpBackgroundImage.data) {
            returnedObj.NggpBackgroundImage = this.urlBase + nggpBackgroundImage.data.attributes.url;
          }
          if (ngpBackgroundImage.data) {
            returnedObj.NgpBackgroundImage = this.urlBase + ngpBackgroundImage.data.attributes.url;
          }
          return returnedObj;
        }
      )
    );
  }

  getOnboardContent(): Observable<any> {
    const endpoint = `${this.apiUrl}page-onboard`;
    return this._http.get(endpoint).pipe(
      map(
        ({
          data: {
            attributes: {
              UserInstruction: userInstruction,
              BusinessSegmentInstruction: businessSegmentInstruction,
              NggpCard: nggpCard,
              NgpIntrastateCard: ngpIntrastateCard,
              NotAnyCard: notAnyCard,
              FormContext: formContext
            }
          }
        }: any) => {
          return {
            UserInstruction: userInstruction,
            BusinessSegmentInstruction: businessSegmentInstruction,
            FormContext: formContext,
            cards: {
              Nggp: this._getConvertedMarkdown(nggpCard),
              NgpIntrastate: this._getConvertedMarkdown(ngpIntrastateCard),
              NotAny: this._getConvertedMarkdown(notAnyCard)
            }
          };
        }
      )
    );
  }

  initAppUser(appUserAttributes: { CrmId: string; Id: number } /* AppUser */): Promise<boolean> {
    this.appUserId = appUserAttributes.Id;
    return Promise.resolve(true);
    /*const handleAllAppUsersResponse = (allAppUsers: AppUserRecord[]): Promise<boolean> => {
      const isExists = allAppUsers.some(
        ({ crmId: crmContactIdFromStrapi }) => crmContactIdFromStrapi === appUserAttributes.CrmId
      );
      const matchedId =
        isExists &&
        allAppUsers.find(({ crmId: crmContactIdFromStrapi }) => crmContactIdFromStrapi === appUserAttributes.CrmId)?.id;
      return isExists
        ? this._getAppUser(matchedId as number)
            .then(({ id: foundId }) => {
              this.appUserId = foundId;
              return Promise.resolve(true);
            })
            .catch(getAppUserError => {
              return Promise.reject(getAppUserError);
            })
        : this._createAppUser(appUserAttributes)
            .then(({ id: createdId }) => {
              this.appUserId = createdId;
              return Promise.resolve(true);
            })
            .catch(createAppUserError => {
              return Promise.reject(createAppUserError);
            });
    };
    return !this.appUserId ? this._getAllAppUsers().then(handleAllAppUsersResponse) : Promise.resolve(true);*/
  }

  getAdminContent(): Observable<any> {
    const endpoint = `${this.apiUrl}page-admin?populate=ContactStatus`;
    return this._http
      .get<{ data: { attributes: { ContactStatus: StrapiContactStatusItem[]; Heading: string } } }>(endpoint)
      .pipe(
        map(
          ({
            data: {
              attributes: { ContactStatus: contactStatus, Heading: heading }
            }
          }) => {
            return {
              Heading: heading,
              ContactStatus: contactStatus.map(
                ({ Name: name, ColorBg: colorBg, ColorFg: colorFg, Label: label, Description: description }) => {
                  return {
                    Name: name,
                    ColorBg: colorBg,
                    ColorFg: colorFg,
                    Label: label,
                    Description: this._getConvertedMarkdown(description)
                  };
                }
              )
            };
          }
        )
      );
  }

  getProfileContent(): Observable<any> {
    const endpoint = `${this.apiUrl}page-my-profile`;
    return this._http.get(endpoint).pipe(
      map(
        ({
          data: {
            attributes: { Heading: heading }
          }
        }: any) => {
          return { Heading: heading };
        }
      )
    );
  }

  getGatheringAndProcessingCards(): Observable<any> {
    const endpoint = `${this.apiUrl}page-gathering-and-processing?populate[FeatureCard][populate]=Icon`;
    return this._http.get<{ data: { attributes: { FeatureCard: StrapiFeatureCardItem[] } } }>(endpoint).pipe(
      map(
        ({
          data: {
            attributes: { FeatureCard: featureCard }
          }
        }) => {
          return featureCard.map(({ Name: name, Description: description, Role: role, URL: url, Icon: icon }) => {
            const iconUrl = icon?.data?.attributes?.url ?? '';
            return {
              Name: name,
              Role: role,
              URL: url,
              iconUrl: iconUrl ? this.urlBase + iconUrl : '',
              Description: this._getConvertedMarkdown(description)
            };
          });
        }
      )
    );
  }

  getPipelinesCards(): Observable<any> {
    const endpoint = `${this.apiUrl}page-pipeline?populate[FeatureCard][populate]=Icon`;
    return this._http.get<{ data: { attributes: { FeatureCard: StrapiFeatureCardItem[] } } }>(endpoint).pipe(
      map(
        ({
          data: {
            attributes: { FeatureCard: featureCard }
          }
        }) => {
          return featureCard.map(({ Name: name, Description: description, Role: role, URL: url, Icon: icon }) => {
            const iconUrl = icon?.data?.attributes?.url ?? '';
            return {
              Name: name,
              Role: role,
              URL: url,
              iconUrl: iconUrl ? this.urlBase + iconUrl : '',
              Description: this._getConvertedMarkdown(description)
            };
          });
        }
      )
    );
  }

  getPipelinesContent(): Observable<any> {
    const endpoint = `${this.apiUrl}pipelines-request`;
    return this._http.get(endpoint).pipe(
      map(
        ({
          data: {
            attributes: {
              NotePrimary: notePrimary,
              AccessHeading: accessHeading,
              AccessDescription: accessDescription,
              AccessInstruction: accessInstruction,
              NoticesHeading: noticesHeading
            }
          }
        }: any) => {
          return {
            NotePrimary: this._getConvertedMarkdown(notePrimary),
            AccessHeading: accessHeading,
            AccessDescription: accessDescription,
            AccessInstruction: accessInstruction,
            NoticesHeading: noticesHeading
          };
        }
      )
    );
  }

  getMeasuredVolumesContent(): Observable<any> {
    const endpoint = `${this.apiUrl}gp-measured-volume`;
    return this._http.get(endpoint).pipe(
      map(
        ({
          data: {
            attributes: {
              SingleMeterHeading: singleMeterHeading,
              SingleMeterInstruction: singleMeterInstruction,
              AllMetersHeading: allMetersHeading,
              AllMetersInstruction: allMetersInstruction,
              AllMetersButtonLabelCreate: allMetersButtonLabelCreate,
              AllMetersButtonLabelView: allMetersButtonLabelView
            }
          }
        }: any) => {
          return {
            SingleMeterHeading: singleMeterHeading,
            SingleMeterInstruction: singleMeterInstruction,
            AllMetersHeading: allMetersHeading,
            AllMetersInstruction: allMetersInstruction,
            AllMetersButtonLabelCreate: allMetersButtonLabelCreate,
            AllMetersButtonLabelView: allMetersButtonLabelView
          };
        }
      )
    );
  }

  getGasSettlementContent(): Observable<any> {
    const endpoint = `${this.apiUrl}gp-gas-settlement`;
    return this._http.get(endpoint).pipe(
      map(
        ({
          data: {
            attributes: {
              SingleStatementHeading: singleStatementHeading,
              SingleStatementInstruction: singleStatementInstruction,
              SettlementDownloadHeading: settlementDownloadHeading,
              SettlementDownloadInstruction: settlementDownloadInstruction
            }
          }
        }: any) => {
          return {
            SingleStatementHeading: singleStatementHeading,
            SingleStatementInstruction: singleStatementInstruction,
            SettlementDownloadHeading: settlementDownloadHeading,
            SettlementDownloadInstruction: settlementDownloadInstruction
          };
        }
      )
    );
  }

  getCheckDetailContent(): Observable<any> {
    const endpoint = `${this.apiUrl}gp-check-detail`;
    return this._http.get(endpoint).pipe(
      map(
        ({
          data: {
            attributes: { Heading: heading, Instruction: instruction }
          }
        }: any) => {
          return { Heading: heading, Instruction: instruction };
        }
      )
    );
  }

  getNggpDocumentsContent(): Observable<any> {
    const endpoint = `${this.apiUrl}gp-document?populate[Document][populate]=DocumentFile`;
    return this._http
      .get<{
        data: { attributes: { SectionTitle: string; SectionBody?: string; Document: StrapiDocumentItem[] } };
      }>(endpoint)
      .pipe(
        map(
          ({
            data: {
              attributes: { SectionTitle: sectionTitle, SectionBody: sectionBody, Document: document }
            }
          }) => {
            return {
              title: sectionTitle,
              description: sectionBody ? this._getConvertedMarkdown(sectionBody) : null,
              documents: document.map(
                ({
                  DocumentName: documentName,
                  DocumentContext: documentContext,
                  DocumentUrl: urlExternal,
                  DocumentFile: documentFile
                }) => {
                  const documentUrl = urlExternal ?? `${this.urlBase}${documentFile?.data?.attributes?.url ?? ''}`;
                  return {
                    name: documentName,
                    url: documentUrl,
                    context: documentContext
                  };
                }
              )
            };
          }
        )
      );
  }

  getNggpRequestsContent(): Observable<any> {
    const endpoint = `${this.apiUrl}gp-request`;
    return this._http.get(endpoint).pipe(
      map(
        ({
          data: {
            attributes: {
              NotShipperOperatorNote: notShipperOperatorNote,
              AgentNote: agentNote,
              Heading: heading,
              Description: description
            }
          }
        }: any) => {
          return {
            AgentNote: agentNote,
            Heading: heading,
            Description: description,
            NotShipperOperatorNote: this._getConvertedMarkdown(notShipperOperatorNote)
          };
        }
      )
    );
  }

  getSwitchAccountsContent(): Observable<any> {
    const endpoint = `${this.apiUrl}global?fields[0]=SwitchAccounts`;
    return this._http.get(endpoint).pipe(
      map(
        ({
          data: {
            attributes: { SwitchAccounts: switchAccounts }
          }
        }: any) => {
          return this._getConvertedMarkdown(switchAccounts);
        }
      )
    );
  }

  getAllAppOrientations(): Observable<any> {
    const endpoint = `${this.apiUrl}app-orientations?fields[0]=Url&fields[1]=Title&fields[2]=Body`;
    return this._http
      .get<{
        data: { id: number; attributes: { Url: string; Title: string; Body: string; Meta: StrapiMetaItem[] } }[];
      }>(endpoint)
      .pipe(
        map(({ data: appOrientations }) =>
          appOrientations.map(({ id, attributes: { Url: url, Title: title, Body: body, Meta: meta } }) => {
            return {
              id,
              Url: url,
              Title: title,
              Meta: meta,
              Body: this._getConvertedMarkdown(body)
            };
          })
        )
      );
  }

  getAppOrientation(appOrientationId: number): Observable<any> {
    const endpoint = `${this.apiUrl}app-orientations/${appOrientationId}?fields=Meta`;
    return this._http.get<{ data: { id: number; attributes: { Meta: StrapiMetaItem[] } } }>(endpoint).pipe(
      map(
        ({
          data: {
            id,
            attributes: { Meta: meta }
          }
        }) => {
          return { id, Meta: meta };
        }
      )
    );
  }

  putAppOrientation(appOrientationId: number, updatedMeta: StrapiMetaItem[]): Observable<any> {
    const endpoint = `${this.apiUrl}app-orientations/${appOrientationId}`;
    return this._http
      .put<{ data: { id: number; attributes: { Meta: StrapiMetaItem[] } } }>(endpoint, {
        data: {
          Meta: updatedMeta
        }
      })
      .pipe(
        map(
          ({
            data: {
              id,
              attributes: { Meta: meta }
            }
          }) => {
            return { id, Meta: meta };
          }
        )
      );
  }

  doAppOrientationUpdate(appOrientationId: number): Observable<any> {
    /* Method 1: switchMap
    return this.getNewPortalUserCardFromStrapi( newPortalUserCardId ).pipe(
      switchMap( ({ id: cardId1, PortalUsersHaveCleared: userIds1 }) => cardId1
        ? this.putNewPortalUserCardFromStrapi( cardId1, [ ...userIds1, this.portalUserId ] ).pipe(
          map(
            ({ id: cardId2, PortalUsersHaveCleared: userIds2 }) => {
              return {
                id: cardId2,
                PortalUsersHaveCleared: userIds2
              }
            }
          ),
          catchError(
            () => of({ id: cardId1, PortalUsersHaveCleared: userIds1 })
          )
        )
        : of({ id: null, PortalUsersHaveCleared: null })
      )
    ); */
    // Method 2: concatMap
    return this.getAppOrientation(appOrientationId).pipe(
      concatMap(({ id, Meta: meta }) =>
        this.putAppOrientation(id, [
          ...meta,
          {
            id: this.appUserId as number,
            acknowledgedAt: new Date().toISOString()
          }
        ])
      )
    );
  }

  getNotifications(): Promise<any> {
    const endpoint = `${this.apiUrl}app-notifications`;
    return lastValueFrom(
      this._http.get<{ data: StrapiNotificationEntry[] }>(endpoint).pipe(
        map(({ data: notifications }) =>
          notifications
            .filter(({ attributes }) => attributes.IsActive)
            .map(
              ({
                attributes: {
                  Body: body,
                  Title: title,
                  ViewUrl: viewUrl,
                  Meta: meta,
                  IsActive: isActive,
                  IsViewStatusSaved: isViewStatusSaved,
                  createdAt,
                  updatedAt
                },
                id
              }) => ({
                id,
                createdAt: createdAt ? new Date(createdAt) : new Date(),
                updatedAt: updatedAt ? new Date(updatedAt) : new Date(),
                ...(typeof body !== 'undefined'
                  ? {
                      Body: this._getConvertedMarkdown(body)
                    }
                  : {}),
                ...(typeof title !== 'undefined'
                  ? {
                      Title: title
                    }
                  : {}),
                ...(typeof isActive !== 'undefined'
                  ? {
                      IsActive: isActive
                    }
                  : {}),
                ...(typeof isViewStatusSaved !== 'undefined'
                  ? {
                      IsViewStatusSaved: isViewStatusSaved
                    }
                  : {}),
                ...(typeof viewUrl === 'string'
                  ? {
                      ViewUrl: viewUrl
                    }
                  : {}),
                ...(typeof meta !== 'undefined'
                  ? {
                      Meta: meta
                    }
                  : {})
              })
            )
        )
      )
    );
  }

  getAppNotification(appNotificationId: number): Observable<any> {
    const endpoint = `${this.apiUrl}app-notifications/${appNotificationId}?fields=Meta`;
    return this._http.get<{ data: { id: number; attributes: { Meta: StrapiMetaItem[] } } }>(endpoint).pipe(
      map(
        ({
          data: {
            id,
            attributes: { Meta: meta }
          }
        }) => ({
          id,
          Meta: meta
        })
      )
    );
  }

  putAppNotification(appNotificationId: number, updatedMeta: StrapiMetaItem[]): Observable<any> {
    const endpoint = `${this.apiUrl}app-notifications/${appNotificationId}`;
    return this._http
      .put<{ data: { id: number; attributes: { Meta: StrapiMetaItem[] } } }>(endpoint, {
        data: {
          Meta: updatedMeta
        }
      })
      .pipe(
        map(
          ({
            data: {
              id,
              attributes: { Meta: meta }
            }
          }) => ({
            id,
            Meta: meta
          })
        )
      );
  }

  doAppNotificationUpdate(notificationId: number): Observable<any> {
    // If appUserId is available immediately, proceed directly
    if (this.appUserId) {
      return this.getAppNotification(notificationId).pipe(
        concatMap(({ id, Meta: meta }) => {
          return this.putAppNotification(id, [
            ...(meta ?? []),
            {
              id: this.appUserId as number,
              acknowledgedAt: new Date().toISOString()
            }
          ]);
        })
      );
    }

    // Otherwise wait for appUserId to be available
    return this.appUserId$.pipe(
      take(1),
      switchMap(appUserId => {
        return this.getAppNotification(notificationId).pipe(
          concatMap(({ id, Meta: meta }) => {
            return this.putAppNotification(id, [
              ...(meta ?? []),
              {
                id: appUserId,
                acknowledgedAt: new Date().toISOString()
              }
            ]);
          })
        );
      })
    );
  }

  get notifications(): AppNotification[] {
    return this.appNotifications || [];
  }

  set notifications(appNotifications: AppNotification[]) {
    this.appNotifications = appNotifications;
  }

  get orientations(): AppOrientation[] {
    return this.appOrientations || [];
  }

  set orientations(appOrientations: AppOrientation[]) {
    this.appOrientations = appOrientations;
  }

  getHelpContent(): Observable<any> {
    const endpoint = `${this.apiUrl}page-help?populate[help_sections][populate]=DisplayName,Description,HelpFeature.Faqs,HelpFeature.Contacts`;
    return this._http.get<{ data: { attributes: { help_sections: { data: StrapiHelpSection[] } } } }>(endpoint).pipe(
      map(
        ({
          data: {
            attributes: {
              help_sections: { data: sections }
            }
          }
        }) => {
          return sections.map(
            ({ attributes: { Description: description, DisplayName: displayName, HelpFeature: helpFeature } }) => {
              return {
                topic: displayName,
                description: this._getConvertedMarkdown(description),
                features: helpFeature.map(({ FeatureName: featureName, Faqs: faqs, Contacts: contacts }) => {
                  const feature: any = {
                    name: this._getConvertedMarkdown(featureName).replace('<p>', '').replace('</p>', '')
                  };
                  if (faqs) {
                    feature.type = 'faq';
                    feature.faqs = faqs
                      .map(({ Question: question, Answer: answer, Category: category }) => {
                        return {
                          q: question,
                          cat: category,
                          a: this._getConvertedMarkdown(answer.replace(/[^/\n]\n[^/\n]/g, '<br>'))
                        };
                      })
                      .reduce(
                        (
                          sanitizedFaqs: Record<string, { cat: string; pairs: { q: string; a: string }[] }>,
                          { cat, q, a }
                        ) => {
                          const safeKey = cat.replace(/\s/g, '');
                          if (!sanitizedFaqs[safeKey]) {
                            sanitizedFaqs[safeKey] = {
                              cat,
                              pairs: []
                            };
                          }
                          sanitizedFaqs[safeKey].pairs.push({ q, a });
                          return sanitizedFaqs;
                        },
                        {}
                      );
                  } else if (contacts) {
                    feature.type = 'contacts';
                    feature.contacts = contacts.map(
                      ({
                        FullName: name,
                        Title: title,
                        Area: area,
                        Group: group,
                        Email: email,
                        PhoneMobile: phoneMobile,
                        PhoneOffice: phoneOffice
                      }) => {
                        return {
                          name,
                          title,
                          area,
                          group,
                          email,
                          phoneMobile,
                          phoneOffice
                        };
                      }
                    );
                  }
                  return feature;
                })
              };
            }
          );
        }
      )
    );
  }
}
